import { useEffect, useState } from 'react';

const LS_KEY = 'assetlens_scoring_prefs';

function loadLocalWeights() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Single source of truth for "which scoring weights does this user have?"
 *
 * Used by Properties (list) and PropertyDetail so that "Your Score" stays
 * consistent across views — no more 67-on-the-list-but-36-on-detail
 * surprises.
 *
 * If the user is logged in we fetch from /api/account/profile.
 * Otherwise we fall back to localStorage (anonymous users still get the
 * scoring slider experience for the duration of their browser session).
 *
 * Returns:
 *   { weights, hasWeights }
 *
 * `hasWeights` is true once we have a non-default config — that's the
 * signal to render "Your Score" rather than the raw global score.
 */
export default function useScoringWeights() {
  const [weights, setWeights]       = useState(null);
  const [hasWeights, setHasWeights] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('assetlens_token');
    let cancelled = false;

    if (token) {
      fetch('/api/account/profile', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : null))
        .then(profile => {
          if (cancelled) return;
          if (profile?.scoring_preferences) {
            try {
              const parsed = JSON.parse(profile.scoring_preferences);
              setWeights(parsed);
              setHasWeights(true);
              return;
            } catch { /* fall through */ }
          }
          // No saved prefs server-side — try localStorage as last resort
          const local = loadLocalWeights();
          if (local) { setWeights(local); setHasWeights(true); }
        })
        .catch(() => {
          const local = loadLocalWeights();
          if (!cancelled && local) { setWeights(local); setHasWeights(true); }
        });
    } else {
      const local = loadLocalWeights();
      if (local) { setWeights(local); setHasWeights(true); }
    }

    return () => { cancelled = true; };
  }, []);

  return { weights, hasWeights };
}
